import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './context/AuthContext.jsx';
import { EmployeeProvider } from './context/EmployeeContext.jsx';
import { PermissionProvider } from './context/PermissionContext.jsx';
import ProtectedRoute from './components/layout/ProtectedRoute.jsx';
import MainLayout from './components/layout/MainLayout.jsx';
import LoadingSpinner from './components/common/LoadingSpinner';

// Eager load critical pages
import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';

// Lazy load non-critical pages for better performance
const EmployeeDirectory = lazy(() => import('./pages/EmployeeDirectory.jsx'));
const EmployeeDetail = lazy(() => import('./pages/EmployeeDetail.jsx'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile.jsx'));
const CreateEmployee = lazy(() => import('./pages/CreateEmployee.jsx'));
const EditEmployee = lazy(() => import('./pages/EditEmployee.jsx'));
const Projects = lazy(() => import('./pages/Projects.jsx'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails.jsx'));
const Skills = lazy(() => import('./pages/Skills.jsx'));
const KRA = lazy(() => import('./pages/KRA.jsx'));
const GoalsAndMilestonesStandalone = lazy(() => import('./pages/GoalsAndMilestonesStandalone.jsx'));
const EmployeeTree = lazy(() => import('./pages/EmployeeTree.jsx'));
const AuditLog = lazy(() => import('./pages/AuditLog.jsx'));
const AdminLogin = lazy(() => import('./pages/AdminLogin.jsx'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard.jsx'));
const Culture = lazy(() => import('./pages/culture/Culture.jsx'));
const CompareEmployeeUpdate = lazy(() => import('./pages/CompareEmployeeUpdate.jsx'));
const PreviewEmployeeUpdate = lazy(() => import('./pages/PreviewEmployeeUpdate.jsx'));

// Styles
import './styles/index.scss';

// Optimized loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen bg-slate-100">
    <LoadingSpinner message="Loading page..." fullScreen />
  </div>
);

const App = () => {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <AuthProvider>
        <EmployeeProvider>
          <PermissionProvider>
            <Router>
                <Routes>
                  {/* Public Routes - No Layout */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/admin/login" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminLogin />
                    </Suspense>
                  } />
                  
                  {/* Protected Routes - Wrapped in ProtectedRoute and MainLayout */}
                  <Route element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <MainLayout />
                      </Suspense>
                    </ProtectedRoute>
                  }>
                    <Route path="/" element={<Navigate to="/workforce" replace />} />
                    <Route path="/workforce" element={<Dashboard />} />
                    <Route path="/directory" element={<EmployeeDirectory />} />
                    <Route path="/employee/:id" element={<EmployeeDetail />} />
                    <Route path="/employee-profile/:id" element={<EmployeeProfile />} />
                    <Route path="/employee/create" element={<CreateEmployee />} />
                    <Route path="/employee/:id/edit" element={<EditEmployee />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:id" element={<ProjectDetails />} />
                    <Route path="/project/:id" element={<ProjectDetails />} />
                    <Route path="/skills" element={<Skills />} />
                    <Route path="/kra/*" element={<KRA />} />
                    <Route path="/goals-milestones" element={<GoalsAndMilestonesStandalone />} />
                    <Route path="/employee-tree" element={<EmployeeTree />} />
                    <Route path="/culture/*" element={<Culture />} />
                    <Route path="/audit-log" element={<AuditLog />} />
                    <Route path="/pending-updates/:updateId/compare" element={<CompareEmployeeUpdate />} />
                    <Route path="/pending-updates/:updateId/preview" element={<PreviewEmployeeUpdate />} />
                  </Route>

                  {/* Admin Dashboard - Handles its own auth and layout */}
                  <Route path="/admin/dashboard" element={
                    <Suspense fallback={<PageLoader />}>
                      <AdminDashboard />
                    </Suspense>
                  } />
                  
                  {/* Catch all */}
                  <Route path="*" element={<Navigate to="/workforce" replace />} />
                </Routes>
              </Router>
          </PermissionProvider>
        </EmployeeProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
